'use client'

import { useCommutePlaces, DEFAULT_PLACES, type CommutePlace } from './store'
import { getUserProfile, updateUserCommutePlaces } from '@/lib/firebase'

// Account persistence for navigation targets. The store itself stays
// localStorage-first (offline / guest / optimistic); this layer mirrors it to
// the signed-in user's Firestore doc so targets survive re-login on another web
// device — the same client-Firestore pattern the Must-haves card uses.
//
// Note: this syncs cross-DEVICE on web. It does NOT yet sync with the mobile app,
// which stores its own `users/{uid}.prefs.places` under a different path/shape —
// true cross-platform sync needs a shared schema (tracked separately).

let syncUid: string | null = null
let lastSerialized: string | null = null
let writeTimer: ReturnType<typeof setTimeout> | null = null

const serialize = (places: CommutePlace[]) => JSON.stringify(places)

// Subscribe once (module load): whenever a signed-in user's targets change,
// debounce a write. The lastSerialized guard skips the echo write when the
// change was itself a hydrate from the account.
useCommutePlaces.subscribe((state) => {
  if (!syncUid) return
  const ser = serialize(state.places)
  if (ser === lastSerialized) return
  lastSerialized = ser
  const uid = syncUid
  if (writeTimer) clearTimeout(writeTimer)
  writeTimer = setTimeout(() => {
    writeTimer = null
    void updateUserCommutePlaces(uid, state.places as unknown as Array<Record<string, unknown>>).catch((e) =>
      console.error('commute sync write failed', e)
    )
  }, 800)
})

// Cross-tab: when another tab persists a change (localStorage 'storage' event),
// adopt its serialization as the baseline BEFORE this tab's store rehydrates, so
// the rehydrate's setPlaces doesn't re-write the identical data to Firestore.
// Key must match STORAGE_KEY in ./store.ts.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== 'roostr-commute-places' || !e.newValue) return
    try {
      const places = JSON.parse(e.newValue)?.state?.places
      if (Array.isArray(places)) lastSerialized = serialize(places)
    } catch {
      /* malformed — fall back to the (idempotent) redundant write */
    }
  })
}

/**
 * Call on sign-in. Adopts the account's saved targets as the source of truth;
 * if the account has none yet, seeds it from the current local targets so a
 * guest's setup is preserved.
 */
export async function beginCommuteSync(uid: string): Promise<void> {
  // Disarm while hydrating: the hydrate's own setPlaces must not write back,
  // and — critically — a FAILED load must never leave sync armed, or a later
  // edit would clobber the account's real targets with local/demo data. Also
  // drops any write still queued for a previous uid (account switch).
  syncUid = null
  if (writeTimer) {
    clearTimeout(writeTimer)
    writeTimer = null
  }
  try {
    const profile = await getUserProfile(uid)
    const acc = (profile?.commutePlaces ?? null) as CommutePlace[] | null
    const store = useCommutePlaces.getState()
    if (Array.isArray(acc) && acc.length > 0) {
      // The account is the source of truth for a signed-in user.
      store.setPlaces(acc)
    } else {
      // Fresh account (no saved targets) → start from clean defaults. Never
      // auto-seed from the local store: on a shared browser it can hold the
      // previous user's private addresses (leak), or just the demo defaults
      // (pollution). Targets only reach the account once the user edits them.
      store.setPlaces(DEFAULT_PLACES)
    }
    // Snapshot AFTER setPlaces (which caps to MAX_PLACES) so the echo guard
    // matches the real store state; arm only now that hydration succeeded.
    lastSerialized = serialize(useCommutePlaces.getState().places)
    syncUid = uid
  } catch (e) {
    syncUid = null
    console.error('commute sync hydrate failed', e)
  }
}

/** Call on sign-out — stop mirroring and forget the last-synced snapshot. */
export function endCommuteSync(): void {
  syncUid = null
  lastSerialized = null
  if (writeTimer) {
    clearTimeout(writeTimer)
    writeTimer = null
  }
}
